// src/codebase/pages/RepoListPage.jsx
//
// Repository list. Route: /codebase.
//
// Same structure as admin/pages/UsersPage.jsx: PageHeader with the action on
// the right, a DataTable, create/edit behind a modal, deactivate behind
// ConfirmModal. Replaces the old hand-rolled <table> + separate /repos/new
// and /repos/:id/edit routes.
//
// No connections table here — per explicit product direction, Codebase
// Analysis doesn't manage connections itself; that's /connectors (labelled
// "Connections" in the nav). The "Add Repository" modal's connection picker
// points there when empty.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GitBranch, Plus, ArrowRight, Activity, AlertTriangle } from "lucide-react";
import { PageHeader, Button, EmptyState, DataTable, ScoreChip, StatTile, ConfirmModal, SkeletonCardGrid, SkeletonTable } from "../../shared/ui";
import { repoApi } from "../../api/repoApi";
import { analysisApi } from "../../api/analysisApi";
import { getApiError } from "../../api/client";
import { useWorkspace } from "../../context/WorkspaceContext";
import RepoFormModal from "../components/RepoFormModal.jsx";

export default function RepoListPage() {
  const { currentEngagementId } = useWorkspace();
  const navigate = useNavigate();

  const [repos, setRepos] = useState([]);
  const [healthMap, setHealthMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // null = closed. { repo: null } = create, { repo: rec } = edit.
  const [formTarget, setFormTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  const refresh = useCallback(async () => {
    if (!currentEngagementId) {
      setRepos([]);
      setHealthMap({});
      setLoading(false);
      setLoadError(null);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const res = await repoApi.list(currentEngagementId, false);
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setRepos(items);

      const entries = await Promise.allSettled(items.map((r) => analysisApi.getRepoHealth(r.id)));
      const hm = {};
      entries.forEach((p, idx) => {
        if (p.status === "fulfilled") hm[items[idx].id] = p.value.data;
      });
      setHealthMap(hm);
    } catch (err) {
      setLoadError(getApiError(err));
    } finally {
      setLoading(false);
    }
  }, [currentEngagementId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSubmit = useCallback(
    async (payload) => {
      const editing = formTarget?.repo;
      const res = editing
        ? await repoApi.update(editing.id, payload)
        : await repoApi.create(payload);
      await refresh();
      return res.data;
    },
    [formTarget, refresh]
  );

  const handleDeactivate = useCallback(async () => {
    await repoApi.deactivate(deactivateTarget.id);
    setDeactivateTarget(null);
    await refresh();
  }, [deactivateTarget, refresh]);

  // Same KPI-row pattern as UsersPage/AccountsPage/RolesPage — a bare table
  // read as too plain next to those pages' StatTile summary (see
  // shared/ui/StatTile.jsx's own history note).
  const { avgHealth, attentionCount } = useMemo(() => {
    const scores = repos
      .map((r) => healthMap[r.id]?.health_score)
      .filter((v) => typeof v === "number");
    return {
      avgHealth: scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null,
      attentionCount: scores.filter((v) => v < 50).length,
    };
  }, [repos, healthMap]);

  const columns = [
    {
      accessorKey: "name",
      header: "Repository",
      cell: ({ row }) => (
        <div className="min-w-0">
          <span className="block font-medium text-ink">{row.original.name}</span>
          {row.original.description && (
            <span className="block text-xs text-muted mt-0.5">{row.original.description}</span>
          )}
        </div>
      ),
    },
    { accessorKey: "provider", header: "Provider", width: 130 },
    {
      accessorKey: "default_branch",
      header: "Branch",
      width: 130,
      cell: ({ getValue }) => <span className="font-mono text-xs">{getValue() || "main"}</span>,
    },
    {
      id: "lastScan",
      header: "Last Scan",
      width: 180,
      accessorFn: (row) => healthMap[row.id]?.last_scan_at || "",
      cell: ({ row }) => {
        const at = healthMap[row.original.id]?.last_scan_at;
        return <span className="text-xs text-muted">{at ? new Date(at).toLocaleString() : "—"}</span>;
      },
    },
    {
      id: "health",
      header: "Health",
      width: 100,
      align: "center",
      accessorFn: (row) => healthMap[row.id]?.health_score ?? 0,
      cell: ({ getValue }) => <ScoreChip score={getValue()} />,
    },
    {
      id: "actions",
      header: "",
      align: "right",
      width: 220,
      sortAllowed: false,
      cell: ({ row }) => {
        const repo = row.original;
        return (
          <div className="flex items-center justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/codebase/repos/${repo.id}`)}>
              Open <ArrowRight size={14} />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setFormTarget({ repo })}>
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700"
              onClick={() => setDeactivateTarget(repo)}
            >
              Deactivate
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title="Repositories"
        subtitle="Connect GitHub / Azure DevOps / Bitbucket / ServiceNow repositories for code analysis."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => navigate("/connectors")}>
              Manage Connections
            </Button>
            <Button variant="primary" onClick={() => setFormTarget({ repo: null })}>
              <Plus size={15} /> Add Repository
            </Button>
          </div>
        }
      />

      {!loadError && (loading || repos.length > 0) && (
        loading ? (
          <SkeletonCardGrid cards={3} className="mb-5" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <StatTile icon={GitBranch} label="Total Repositories" value={repos.length} />
            <StatTile
              icon={Activity}
              label="Average Health"
              value={avgHealth === null ? "—" : avgHealth}
              hint={avgHealth === null ? "No scans yet" : undefined}
            />
            <StatTile
              icon={AlertTriangle}
              label="Needs Attention"
              value={attentionCount}
              hint={attentionCount > 0 ? "Health score below 50" : undefined}
              tone={attentionCount > 0 ? "danger" : "brand"}
            />
          </div>
        )
      )}

      {!currentEngagementId ? (
        <EmptyState
          icon={GitBranch}
          title="No engagement selected"
          description="Select an engagement from the switcher to see its repositories."
        />
      ) : loading ? (
        <SkeletonTable rows={5} columns={6} className="rounded-card border border-line bg-white" />
      ) : loadError ? (
        <EmptyState
          icon={GitBranch}
          title="Couldn't load repositories"
          description={loadError}
          action={
            <Button variant="secondary" onClick={refresh}>
              Try again
            </Button>
          }
        />
      ) : repos.length === 0 ? (
        <EmptyState
          icon={GitBranch}
          title="No repositories yet"
          description="Register a repository to start analyzing its code."
          action={
            <Button variant="secondary" onClick={() => setFormTarget({ repo: null })}>
              <Plus size={15} /> Add Repository
            </Button>
          }
        />
      ) : (
        <DataTable table={{ columns, rows: repos }} canSearch />
      )}

      <RepoFormModal
        key={formTarget?.repo?.id ?? "new"}
        open={Boolean(formTarget)}
        repo={formTarget?.repo || null}
        engagementId={currentEngagementId}
        onClose={() => setFormTarget(null)}
        onSubmit={handleSubmit}
      />

      <ConfirmModal
        open={Boolean(deactivateTarget)}
        entityLabel="repository"
        name={deactivateTarget?.name}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={handleDeactivate}
      >
        Its scan history is kept, but it stops appearing in this list and can no longer be scanned.
      </ConfirmModal>
    </div>
  );
}
